import React, { useEffect, useState } from "react";
import { auth, db } from "./firebaseConfig";
import { onAuthStateChanged} from "firebase/auth";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { deleteDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./dashboard.css";


const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
   // eslint-disable-next-line
  const [quizzes, setQuizzes] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [personalHistory, setPersonalHistory] = useState([]);
   // eslint-disable-next-line
  const [globalStats, setGlobalStats] = useState({ total: 0, avgScore: 0 });
  const [flashcards, setFlashcards] = useState([]);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("quizzes");
  const [quizAverages, setQuizAverages] = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/login");
      } else {
        setUser(currentUser);
        setLoading(true);


        const userRef = doc(db, "users", currentUser.uid);
        try {
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            setProfile(userSnap.data());
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }

        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchCreatorQuizzesAndAnalytics();
    }
     // eslint-disable-next-line
  }, [user]);

  const fetchCreatorQuizzesAndAnalytics = async () => {
    try {
      const q = query(collection(db, "quizzes"), where("authorId", "==", user.uid));
      const snapshot = await getDocs(q);
      const creatorQuizzes = [];
      const creatorFlashcards = [];

      snapshot.forEach((docSnap) => {
        const data = { id: docSnap.id, ...docSnap.data() };
        if (data.quizType === "flashcards") {
          creatorFlashcards.push(data);
        } else {
          creatorQuizzes.push(data);
        }
      });

      setQuizzes(creatorQuizzes);
      setFlashcards(creatorFlashcards);

      const userSnapshot = await getDocs(collection(db, "users"));
      const userHistories = [];
      userSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        if (userData.quizHistory && userData.quizHistory.length > 0) {
          userData.quizHistory.forEach((attempt) => {
            userHistories.push(attempt);
          });
        }
      });

      const quizStats = creatorQuizzes.map((quiz) => {
        const quizAttempts = userHistories.filter((h) => h.quizId === quiz.id);
        const totalAttempts = quizAttempts.length;
        const avgScore = totalAttempts > 0
          ? quizAttempts.reduce((sum, h) => sum + h.scorePercentage, 0) / totalAttempts
          : 0;
        const passCount = quizAttempts.filter((h) => h.passed).length;
        const failCount = totalAttempts - passCount;

        return {
          title: quiz.title,
          id: quiz.id,
          public: quiz.public,
          attempts: totalAttempts,
          average: avgScore.toFixed(2),
          passCount,
          failCount,
        };
      });

      setAnalytics(quizStats);
    } catch (err) {
      console.error("Error fetching analytics:", err);
    }
  };


  useEffect(() => {
    if (user) {
      fetchPersonalStats();
      fetchGlobalAverage();
      fetchQuizAverages();
    }
     // eslint-disable-next-line
  }, [user]);

  const fetchPersonalStats = async () => {
    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists() && userSnap.data().quizHistory) {
        const history = userSnap.data().quizHistory;

        const updatedHistory = await Promise.all(
          history.map(async (entry) => {
            const quizRef = doc(db, "quizzes", entry.quizId);
            const quizSnap = await getDoc(quizRef);
            const quizTitle = quizSnap.exists() ? quizSnap.data().title : "Deleted Quiz";
            return { ...entry, quizTitle };
          })
        );

        updatedHistory.sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds);

        setPersonalHistory(updatedHistory);
      }
    } catch (error) {
      console.error("Error fetching personal history:", error);
    }
  };

  const fetchGlobalAverage = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "users"));
      let allScores = [];
      usersSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.quizHistory) {
          allScores = allScores.concat(data.quizHistory.map((h) => h.scorePercentage));
        }
      });
      const total = allScores.length;
      const avg = total > 0 ? allScores.reduce((sum, s) => sum + s, 0) / total : 0;
      setGlobalStats({ total, avgScore: avg.toFixed(2) });
    } catch (err) {
      console.error("Error calculating global stats:", err);
    }
  };

  const fetchQuizAverages = async () => {
    try {
      const snapshot = await getDocs(collection(db, "quizzes"));
      const averages = {};
      snapshot.forEach(doc => {
        const data = doc.data();
        averages[doc.id] = data.avgScore || null;
      });
      setQuizAverages(averages);
    } catch (err) {
      console.error("Error fetching quiz averages:", err);
    }
  };


  const handleDeleteQuiz = async (quizId) => {
    if (window.confirm("Are you sure you want to delete this quiz? This action cannot be undone.")) {
      try {
        await deleteDoc(doc(db, "quizzes", quizId));
        alert("Quiz deleted successfully!");

        setQuizzes((prev) => prev.filter((quiz) => quiz.id !== quizId));
        setAnalytics((prev) => prev.filter((quiz) => quiz.id !== quizId));
      } catch (err) {
        console.error("Error deleting quiz:", err);
        alert("Failed to delete the quiz. Please try again.");
      }
    }
  };

  return (
    <>
      <Navbar />
      <div className="dashboard-container">
        <div className="dashboard-tabs">
          <button className={activeTab === "profile" ? "tab active" : "tab"} onClick={() => setActiveTab("profile")}>Profile</button>
          <button className={activeTab === "quizzes" ? "tab active" : "tab"} onClick={() => setActiveTab("quizzes")}>Your Quizzes</button>
          <button className={activeTab === "flashcards" ? "tab active" : "tab"} onClick={() => setActiveTab("flashcards")}>Flashcards</button>
          <button className={activeTab === "history" ? "tab active" : "tab"} onClick={() => setActiveTab("history")}>History</button>
        </div>

        <h2 className="section-title">Dashboard</h2>

        {loading ? (
          <p>Loading user data...</p>
        ) : user && profile ? (
          <>
            {activeTab === "profile" && (
              <div className="profile-box">
                <p>Welcome, {profile?.username || user.displayName || "User"}!</p>
                <p>Email: {profile?.email || user.email}</p>
                <p>Bio: {profile.bio || "No Bio yet!"}</p>
                <div className="profile-actions">
                  <button onClick={() => navigate("/edit-profile")}>Edit Profile</button>
                </div>
              </div>
            )}

            {activeTab === "quizzes" && (
              <>
                <h3 className="section-header">Your Quizzes</h3>
                <div className="cards-wrapper">
                  {analytics.length > 0 ? analytics.map((quiz, idx) => (
                    <div key={idx} className="card">
                      <div className="card-info">
                        <span className="badge">{quiz.public ? "Public" : "Private"}</span>
                        <span className="badge standard-badge">Standard</span>
                        <h4>{quiz.title}</h4>
                        <p><strong>Total Attempts:</strong> {quiz.attempts}</p>
                        <p><strong>Average Score:</strong> {quiz.average}%</p>
                        <p><strong>Pass/Fail:</strong> {quiz.passCount} passed / {quiz.failCount} failed</p>
                      </div>
                      <div className="actions">
                        <button onClick={() => navigate(`/view-quiz/${quiz.id}`)}>View</button>
                        <button className="delete-btn" onClick={() => handleDeleteQuiz(quiz.id)}>Delete</button>
                        <button onClick={() => navigate(`/edit-quiz/${quiz.id}`)}>Edit</button>
                      </div>
                    </div>
                  )) : <p>You don't have any quizzes</p>}
                </div>
              </>
            )}

            {activeTab === "flashcards" && (
              <>
                <h3 className="section-header">Your Flashcards</h3>
                <div className="cards-wrapper">
                  {flashcards.length > 0 ? flashcards.map((fc) => (
                    <div key={fc.id} className="card">
                      <div className="card-info">
                        <span className="badge">{fc.public ? "Public" : "Private"}</span>
                        <span className="badge flashcard-badge">Flashcards</span>
                        <h4>{fc.title}</h4>
                      </div>
                      <div className="actions">
                        <button onClick={() => navigate(`/view-quiz/${fc.id}`)}>View</button>
                        <button className="delete-btn" onClick={() => handleDeleteQuiz(fc.id)}>Delete</button>
                        <button onClick={() => navigate(`/edit-flashcards/${fc.id}`)}>Edit</button>
                      </div>
                    </div>
                  )) : <p>No flashcards available.</p>}
                </div>
              </>
            )}

            {activeTab === "history" && (
              <>
                <h3 className="section-header">History</h3>
                <div className="cards-wrapper">
                  {personalHistory.length > 0 ? [...personalHistory]
                    .sort((a, b) => b.Timestamp?.seconds - a.Timestamp?.seconds)
                    .map((entry, idx) => {

                      const avg = quizAverages[entry.quizId];
                      const globalAvg = avg !== null && avg !== undefined ? `${avg.toFixed(2)}` : "N/A";


                      return (
                        <div key={idx} className="card">
                          <div className="card-info">
                            <p><strong>Quiz Title:</strong> {entry.quizTitle}</p>
                            <p><strong>Date:</strong> {entry.Timestamp?.seconds ? new Date(entry.Timestamp.seconds * 1000).toLocaleDateString("de-DE") : "N/A"}</p>
                            <p><strong>Time:</strong> {entry.Timestamp?.toDate().toLocaleTimeString("de-DE")}</p>
                            <p><strong>Your Score:</strong> {entry.scorePercentage.toFixed(2)}%</p>
                            <p><strong>Global Avg Score:</strong> {globalAvg}%</p>
                            <p style={{ color: entry.passed ? "green" : "red" }}>
                              <strong>{entry.passed ? "Passed!" : "Failed!"}</strong>
                            </p>
                          </div>
                          <div className="actions">
                            <button onClick={() => navigate(`/view-quiz/${entry.quizId}`)}>View Quiz</button>
                          </div>
                        </div>
                      );
                    }) : <p>No quiz history available.</p>}
                </div>
              </>
            )}
          </>
        ) : (
          <p>Failed to load user profile.</p>
        )}
      </div>
    </>
  );
};

export default Dashboard;
